if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        /* ---------------------------
         * Shopacado hook integration
         * ---------------------------
         * We push the currently selected variant's price (in cents)
         * to Shopacado whenever the variant changes.
         */

        // helper: fire Shopacado safely
        this._updateShopacado = (variant) => {
          if (!variant || typeof variant.price !== 'number') return;
          if (window.shopacado && typeof window.shopacado.productUpdateWithNewPrice === 'function') {
            try {
              // Shopify JS variant.price is in cents; Shopacado expects cents.
              window.shopacado.productUpdateWithNewPrice(variant.price);
            } catch (e) {
              // console.log(e);
            }
          }
        };

        // 1) Preferred: subscribe to Dawn's pub/sub for variant changes
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this._shopacadoUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
            const variant = payload?.data?.variant;
            this._updateShopacado(variant);
          });
        }

        // 2) Fallback: try to read the currentVariant from variant-radios/selects once on load
        queueMicrotask(() => {
          const variantComponent = document.querySelector('variant-radios, variant-selects');
          const initialVariant = variantComponent?.currentVariant;
          if (initialVariant) this._updateShopacado(initialVariant);
        });
        // (Optional) Extra fallback: listen for changes to the hidden id input (less ideal but robust)
        this._idChangeHandler = () => {
          const variantComponent = document.querySelector('variant-radios, variant-selects');
          if (variantComponent?.currentVariant) this._updateShopacado(variantComponent.currentVariant);
        };
        this.variantIdInput?.addEventListener('change', this._idChangeHandler);
        // ---- end Shopacado wiring ----
      }

      disconnectedCallback() {
        if (this._shopacadoUnsub && typeof this._shopacadoUnsub === 'function') {
          try { this._shopacadoUnsub(); } catch (e) {}
        }
        if (this.variantIdInput && this._idChangeHandler) {
          try { this.variantIdInput.removeEventListener('change', this._idChangeHandler); } catch (e) {}
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
/* --- Shopacado: refresh discount table on variant change --- */
document.addEventListener("variant:changed", function(event) {
  const variant = event?.detail?.variant;
  if (!variant) return;

  document.querySelectorAll(".shopacado-volume-discount").forEach(function(el) {
    el.setAttribute("data-variant-id", variant.id);
    if (window.ShopacadoDiscounts && typeof window.ShopacadoDiscounts.loadDiscounts === "function") {
      window.ShopacadoDiscounts.loadDiscounts();
    }
  });
});
